import { newHttpService } from './new-http-service'

export interface JobRunStatus {
    job_name: string
    job_run_id: string
    system_name: string
    state: 'STARTING' | 'RUNNING' | 'STOPPING' | 'STOPPED' | 'SUCCEEDED' | 'FAILED' | 'TIMEOUT' | 'ERROR' | 'UNKNOWN'
    started_on?: string
    completed_on?: string
    error_message?: string
    execution_time?: number
    aws_region?: string
    // Client metadata: these runs enter the store only after a UI trigger.
    ui_stage?: 'raw' | 'consolidation' | 'rule-manager' | 'handler'
    ui_triggered_at?: number
}

export type JobRunUpdate = Omit<JobRunStatus, 'system_name' | 'ui_stage' | 'ui_triggered_at'>
export const TERMINAL_STATES = ['SUCCEEDED', 'FAILED', 'STOPPED', 'TIMEOUT', 'ERROR']
export const jobRunKey = (run: Pick<JobRunStatus, 'job_name' | 'job_run_id'>) => `${run.job_name}:${run.job_run_id}`
export const isRealGlueRun = (run: JobRunStatus) => !run.job_run_id.startsWith('pending-') && !run.job_run_id.startsWith('lambda-')
const normalizeRunSource = (value: string) => value.toLowerCase().replace(/[\s_-]+/g, '')

/** Select the latest UI trigger per ETL system or Lambda stage, preserving run history. */
export function getLatestUiRuns(runs: JobRunStatus[]): JobRunStatus[] {
    const latest = new Map<string, JobRunStatus>()
    const triggerTime = (run: JobRunStatus) => run.ui_triggered_at ?? (Date.parse(run.started_on || '') || 0)
    for (const run of runs) {
        // Placeholders represent an in-flight UI request, not a completed trigger.
        if (run.job_run_id.startsWith('pending-')) continue
        const isStage = run.ui_stage ? run.ui_stage !== 'raw' : run.job_run_id.startsWith('lambda-')
        const key = isStage
            ? `stage:${normalizeRunSource(run.ui_stage || run.system_name)}`
            : `raw:${normalizeRunSource(run.system_name)}`
        const previous = latest.get(key)
        // Array order is the trigger order for legacy entries without timestamps.
        if (!previous || triggerTime(run) >= triggerTime(previous)) latest.set(key, run)
    }
    return Array.from(latest.values())
}

export function getUiRunState(runs: JobRunStatus[]): 'running' | 'success' | 'error' | null {
    if (runs.some(run => run.job_run_id.startsWith('pending-'))) return 'running'
    const latest = getLatestUiRuns(runs)
    if (!latest.length) return null
    if (latest.some(run => !TERMINAL_STATES.includes(run.state))) return 'running'
    return latest.some(run => ['FAILED', 'ERROR', 'TIMEOUT'].includes(run.state)) ? 'error' : 'success'
}

const POLL_INTERVAL_MS = 5000

interface MonitorStore {
    getRuns: () => JobRunStatus[]
    applyStatuses: (statuses: JobRunUpdate[]) => void
    subscribe: (listener: () => void) => () => void
}

export function createJobRunMonitor(store: MonitorStore) {
    let consumers = 0
    let timer: ReturnType<typeof setTimeout> | null = null
    let inFlight = false
    let unsubscribe: (() => void) | null = null

    const pendingRuns = () => Array.from(new Map(store.getRuns()
        .filter(run => isRealGlueRun(run) && !TERMINAL_STATES.includes(run.state))
        .map(run => [jobRunKey(run), run])).values())

    const clearTimer = () => {
        if (timer !== null) clearTimeout(timer)
        timer = null
    }

    const poll = async () => {
        if (!consumers || document.hidden || inFlight) return
        const runs = pendingRuns()
        if (!runs.length) return
        inFlight = true
        try {
            const data = await newHttpService.get('/api/backoffice/job-run-status', { runs: runs.map(jobRunKey).join(',') })
            if (consumers) store.applyStatuses(data.statuses || [])
        } catch (error) {
            // Preserve the last known status and check again on the next cycle.
            console.error('Failed to poll job status', error)
        } finally {
            inFlight = false
            if (consumers && !document.hidden && pendingRuns().length) {
                timer = setTimeout(() => { timer = null; void poll() }, POLL_INTERVAL_MS)
            }
        }
    }

    const reconcile = () => {
        if (!consumers || document.hidden || !pendingRuns().length) {
            clearTimer()
            return
        }
        if (timer === null && !inFlight) void poll()
    }

    const onVisibilityChange = () => {
        clearTimer()
        reconcile()
    }

    return {
        start() {
            consumers++
            if (consumers === 1) {
                unsubscribe = store.subscribe(reconcile)
                document.addEventListener('visibilitychange', onVisibilityChange)
                reconcile()
            }
            let stopped = false
            return () => {
                if (stopped) return
                stopped = true
                consumers--
                if (!consumers) {
                    clearTimer()
                    unsubscribe?.()
                    unsubscribe = null
                    document.removeEventListener('visibilitychange', onVisibilityChange)
                }
            }
        },
    }
}
